import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonModalFormComponent } from '@shared/components/commons/common-modal-form/common-modal-form.component';
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { InputPhoneNumberComponent } from '@controls/input-phone-number/input-phone-number.component';
import { InputComponent } from '@controls/input/input.component';
import { ESignApiService } from '@shared/services/e-sign-api.service';
import { CustomFormValidators } from '@shared/validators/custom-form-validators';
import { emailValidatorMsg } from '@shared/validators/custom-form-validators-messages';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, tap } from 'rxjs';
import { UpdateRecipientInfoModel } from '@models/documents/sign-documents';

@Component({
  selector: 'app-change-signatory-info-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    InputPhoneNumberComponent,
    CommonModalFormComponent,
    DisclaimerComponent
],
  templateUrl: 'change-signatory-info-modal.component.html',
  styleUrl: 'change-signatory-info-modal.component.scss'
})
export class ChangeSignatoryInfoModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private eSignService = inject(ESignApiService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  readonly emailValidatorMsg = emailValidatorMsg;

  message = 'Please note: If the recipient details are changed, the email will be re-sent to the updated address, and the previous email will no longer be valid.';

  @Input() envelopeId = '';
  @Input() documentId = '';
  @Input() email = '';
  @Input() phone = '';
  @Input() id = '';
  @Input() fullName = '';
  @Input() resendEmail = true;

  @Output() confirm = new EventEmitter<UpdateRecipientInfoModel>();

  showDisclaimer = false;
  btnText = 'Re-send';


  form = new FormGroup({
    recipientId: new FormControl<string | null>(null, Validators.required),
    fullName: new FormControl<string | null>(null, Validators.required),
    email: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.emailValidator]),
    phone: new FormControl<string | null>(''),
  });
  isLoading = false;

  ngOnInit(): void {
    this.form.patchValue({
      recipientId: this.id,
      fullName: this.fullName,
      email: this.email,
      phone: this.phone,
    });

    this.listenChanges();
  }

  listenChanges(){
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.showDisclaimer = this.form.value.email !== this.email
      });
  }

  get btnTextValue(): string {
    if(!this.resendEmail)
      return 'Save'

    return !this.showDisclaimer ? 'Re-send' : 'Save and Re-send';
  }

  get getToastrMessage(): string {
    if(!this.resendEmail)
      return 'Recipient information has been updated successfully.';

    return !this.showDisclaimer ? 'Recipient information has been updated successfully.' : 'Recipient information has been updated and email has been re-sent.';
  }

  resend(): void {
    this.isLoading = true;
    const updateRecipientInfoModel = this.form.getRawValue() as UpdateRecipientInfoModel;
    updateRecipientInfoModel.envelopeId = this.envelopeId;

    this.eSignService.resendESign(this.documentId, updateRecipientInfoModel)
      .pipe(
        tap(() => {
          this.toastr.success(this.getToastrMessage, "Success");
          this.confirm.emit(updateRecipientInfoModel);
          this.activeModal.close();
        }),
        catchError(() => {
          this.toastr.error("Error happen.", "Error");
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }

  close(): void {
    this.activeModal.close();
  }
}
